/**
 * Pure helpers behind `useGuideHighlight()` (DC-12, contracts §6).
 *
 * URL contract: `?guide=<kebab guide key>` — the key is the `guideKey` literal from
 * `guideAnchorRegistry.ts`; its testid is always `guide-anchor-${key}` (S-1, DC-22).
 * Key resolution (static + dynamic + landing redirects) is owned by
 * `resolveGuideLandingAnchor` in `guideAnchorRegistry.ts` — this module never
 * re-implements it, only maps the result onto the `?guide=` URL contract.
 *
 * No DOM access here so the guides jest project (node env) can test it.
 */
import { resolveGuideLandingAnchor } from '@modules/guides/anchors/guideAnchorRegistry';

export const GUIDE_QUERY_PARAM = 'guide';
export const PULSE_DURATION_MS = 600;
export const PULSE_ITERATIONS = 2;
export const REDUCED_MOTION_OUTLINE_MS = 1200;
export const LOOKUP_ATTEMPTS = 25;
export const LOOKUP_INTERVAL_MS = 200;
export const ACCORDION_SETTLE_MS = 350;

/**
 * Dynamic anchors (contracts §6): the registry lists their factories, not every
 * instance, so the shapes are mirrored here for callers that need to recognise a
 * dynamic key without resolving it (e.g. matching a DOM testid back to a pattern).
 * Actual `?guide=` resolution never re-derives these — it delegates to
 * `resolveGuideAnchorKey`, which owns the canonical pattern list.
 */
export const DYNAMIC_GUIDE_ANCHOR_PATTERNS: readonly RegExp[] = [
  /^analytics-card-[a-z0-9_]+$/,
  /^condition-row-[A-Za-z0-9_-]+-\d+$/,
];

export type GuideParamOutcome =
  | { status: 'absent' }
  | { status: 'empty' }
  | { status: 'repeated' }
  | { status: 'unknown'; key: string }
  | { status: 'ok'; key: string; testId: string };

/**
 * Resolve a raw `?guide=` key to the `data-testid` to highlight, or `null` when the key
 * is unknown (including the camelCase registry property name — S-1: only the
 * kebab `guideKey` literal is ever accepted). A key whose anchor only renders inside a
 * dialog lands on its host anchor (`GUIDE_ANCHOR_LANDING_REDIRECTS`, D-23).
 */
export function resolveGuideAnchorTestId(key: string): string | null {
  const definition = resolveGuideLandingAnchor(key);
  return definition ? definition.testId : null;
}

/** Parse the `?guide=` contract from a raw `location.search` string (AC-047). */
export function parseGuideParam(search: string): GuideParamOutcome {
  const values = new URLSearchParams(search).getAll(GUIDE_QUERY_PARAM);
  if (values.length === 0) return { status: 'absent' };
  if (values.length > 1) return { status: 'repeated' };
  const key = values[0].trim();
  if (!key) return { status: 'empty' };
  const testId = resolveGuideAnchorTestId(key);
  return testId ? { status: 'ok', key, testId } : { status: 'unknown', key };
}

/** Drop every copy of `name` from a query string, keeping the rest intact. */
export function stripQueryParam(search: string, name: string): string {
  const params = new URLSearchParams(search);
  params.delete(name);
  const next = params.toString();
  return next ? `?${next}` : '';
}
